/**
 * Reconstruire la courbe d'une vidéo — **là où vit le suspense** ⟦A-8⟧.
 *
 * L'avenant A-8 déplace la révélation : la publication dit *ce que tu as fait*, la
 * bibliothèque montre *ce que le public en fait*, **jour après jour**. Encore faut-il pouvoir
 * dessiner la partie déjà tombée — et le moteur ne la garde pas : `verserTranche` **retire**
 * (`shift`) chaque tranche versée de `remaining_curve` et incrémente `curve_cursor`. Ce qui
 * reste en mémoire, c'est l'avenir ; le passé n'est plus qu'un compteur.
 *
 * On le reconstruit sans inventer une seule valeur : `construireCourbe` (§6.2) est
 * déterministe et ne dépend que de la classe, de `vues_pic` et — pour les trends — de la
 * phase de la tendance à la publication. Cette phase n'est pas stockée, mais elle est
 * **déductible de la longueur totale** de la courbe (5 jours en émergente, 4 en pic, 3
 * sinon), qui vaut `curve_cursor + remaining_curve.length`. Aucune règle de contenu n'est
 * réécrite ici : on rappelle la fonction du moteur avec les bons arguments.
 *
 * ⚠️ **La forme est reconstruite, les totaux ne le sont jamais.** Les nombres affichés au
 * joueur (`total_views`, `total_subs`) restent ceux que le moteur a versés, reports
 * fractionnaires compris. Ce module ne sert qu'au **tracé**.
 */

import {
  construireCourbe,
  COURBE_TREND_EMERGENTE,
  COURBE_TREND_PIC,
  type TrendPhase,
  type VideoRecord,
} from "@maxime/shared";

export interface CourbeVideo {
  /** La courbe entière, du J+1 au dernier jour de la fenêtre initiale. */
  complete: number[];
  /** Les tranches déjà versées — ce que la bibliothèque révèle en mode « réel ». */
  realisee: number[];
  /** Les tranches encore à tomber : exactement `video.remaining_curve`. */
  aVenir: number[];
  /** Nombre de jours qu'il reste à encaisser (jamais un montant : ce serait divulgâcher). */
  joursRestants: number;
  /** Jour simulé absolu du premier versement (⟦A-8⟧ : jamais de durée relative). */
  premierJour: number;
  /** Jour simulé absolu du dernier versement. */
  dernierJour: number;
}

/**
 * La phase de tendance à la publication, déduite de la longueur de la courbe (§6.2).
 * `undefined` hors trend — `distribution` ne la lit que pour les trends.
 */
function phaseDeduite(video: VideoRecord): TrendPhase | undefined {
  if (video.class !== "trend") return undefined;
  const longueur = video.curve_cursor + video.remaining_curve.length;
  if (longueur === COURBE_TREND_EMERGENTE.length) return "emergente";
  if (longueur === COURBE_TREND_PIC.length) return "pic";
  return "saturee";
}

export function courbeVideo(video: VideoRecord, jourDePublication: number): CourbeVideo {
  const complete = construireCourbe(video.class, video.vues_pic, phaseDeduite(video));
  // Filet de sécurité : si la reconstruction et l'état divergeaient (courbe modifiée par un
  // effet futur, migration de schéma…), on fait confiance à l'état, jamais à la déduction.
  const versees = Math.min(video.curve_cursor, complete.length);

  return {
    complete,
    realisee: complete.slice(0, versees),
    aVenir: [...video.remaining_curve],
    joursRestants: video.remaining_curve.length,
    premierJour: jourDePublication + 1,
    dernierJour: jourDePublication + complete.length,
  };
}
